export const ZONE_BOUNDS = {
  standard: {
    1: { xMin: -8.5, yMin: 34, xMax: -2.833, yMax: 42 },
    2: { xMin: -2.833, yMin: 34, xMax: 2.833, yMax: 42 },
    3: { xMin: 2.833, yMin: 34, xMax: 8.5, yMax: 42 },
    4: { xMin: -8.5, yMin: 26, xMax: -2.833, yMax: 34 },
    5: { xMin: -2.833, yMin: 26, xMax: 2.833, yMax: 34 },
    6: { xMin: 2.833, yMin: 26, xMax: 8.5, yMax: 34 },
    7: { xMin: -8.5, yMin: 18, xMax: -2.833, yMax: 26 },
    8: { xMin: -2.833, yMin: 18, xMax: 2.833, yMax: 26 },
    9: { xMin: 2.833, yMin: 18, xMax: 8.5, yMax: 26 },
    11: { xMin: -14.167, yMin: 18, xMax: -8.5, yMax: 42 },
    12: { xMin: -8.5, yMin: 42, xMax: 8.5, yMax: 50 },
    13: { xMin: -8.5, yMin: 10, xMax: 8.5, yMax: 18 },
    14: { xMin: 8.5, yMin: 18, xMax: 14.167, yMax: 42 },
  },
  "rh-7-zone": {
    1: { xMin: -8.5, xMax: 8.5, yMin: 42, yMax: 46 },
    2: { xMin: -8.5 - 1.45, xMax: -8.5 + 1.45, yMin: 18, yMax: 42 },
    3: { xMin: -8.5, xMax: -2.833, yMin: 18, yMax: 26 },
    4: { xMin: -2.833, xMax: 2.833, yMin: 18, yMax: 26 },
    5: { xMin: -8.5, xMax: 8.5, yMin: 10, yMax: 18 },
    6: { xMin: 2.833, xMax: 8.5, yMin: 18, yMax: 26 },
    7: { xMin: 8.5 - 1.45, xMax: 8.5 + 1.45, yMin: 18, yMax: 42 },
  },
};

const ZONE_TYPE_ALIASES = {
  standard: "standard",
  "rh-7-zone": "rh-7-zone",
};

export const normalizeZoneType = (zoneType = "standard") => {
  if (typeof zoneType !== "string") return "standard";
  const normalized = zoneType.trim().toLowerCase();
  return ZONE_TYPE_ALIASES[normalized] || "standard";
};

export const getZoneBounds = (zoneType = "standard") =>
  ZONE_BOUNDS[normalizeZoneType(zoneType)] || ZONE_BOUNDS.standard;

const rangesOverlap = (aMin, aMax, bMin, bMax) => aMin < bMax && aMax > bMin;

const EXTENDED_BOUNDS_CACHE = new Map();

const buildExtendedZoneBounds = (zoneType = "standard") => {
  const normalizedZoneType = normalizeZoneType(zoneType);
  if (EXTENDED_BOUNDS_CACHE.has(normalizedZoneType)) {
    return EXTENDED_BOUNDS_CACHE.get(normalizedZoneType);
  }

  const baseBounds = getZoneBounds(normalizedZoneType);
  const entries = Object.entries(baseBounds).map(([zone, bounds]) => ({
    zone: Number(zone),
    ...bounds,
  }));

  const expanded = {};
  for (const current of entries) {
    const leftCandidates = entries
      .filter(
        (other) =>
          other.zone !== current.zone &&
          other.xMax <= current.xMin &&
          rangesOverlap(current.yMin, current.yMax, other.yMin, other.yMax)
      )
      .map((other) => other.xMax);

    const rightCandidates = entries
      .filter(
        (other) =>
          other.zone !== current.zone &&
          other.xMin >= current.xMax &&
          rangesOverlap(current.yMin, current.yMax, other.yMin, other.yMax)
      )
      .map((other) => other.xMin);

    const bottomCandidates = entries
      .filter(
        (other) =>
          other.zone !== current.zone &&
          other.yMax <= current.yMin &&
          rangesOverlap(current.xMin, current.xMax, other.xMin, other.xMax)
      )
      .map((other) => other.yMax);

    const topCandidates = entries
      .filter(
        (other) =>
          other.zone !== current.zone &&
          other.yMin >= current.yMax &&
          rangesOverlap(current.xMin, current.xMax, other.xMin, other.xMax)
      )
      .map((other) => other.yMin);

    expanded[current.zone] = {
      xMin: leftCandidates.length ? Math.max(...leftCandidates) : -Infinity,
      xMax: rightCandidates.length ? Math.min(...rightCandidates) : Infinity,
      yMin: bottomCandidates.length ? Math.max(...bottomCandidates) : -Infinity,
      yMax: topCandidates.length ? Math.min(...topCandidates) : Infinity,
    };
  }

  EXTENDED_BOUNDS_CACHE.set(normalizedZoneType, expanded);
  return expanded;
};

export const calculateZoneFromLocation = (x, y, zoneType = "standard") => {
  if (x === null || x === undefined || y === null || y === undefined) {
    return null;
  }

  const bounds = getZoneBounds(zoneType);
  const expandedBounds = buildExtendedZoneBounds(zoneType);

  for (const [zone, zoneBounds] of Object.entries(bounds)) {
    const { xMin, xMax, yMin, yMax } = zoneBounds;
    if (x >= xMin && x <= xMax && y >= yMin && y <= yMax) {
      return parseInt(zone);
    }
  }

  for (const [zone, zoneBounds] of Object.entries(expandedBounds)) {
    const { xMin, xMax, yMin, yMax } = zoneBounds;
    if (x >= xMin && x <= xMax && y >= yMin && y <= yMax) {
      return parseInt(zone);
    }
  }

  return null;
};

export const getPitchLocation = (pitch) => {
  if (!pitch || typeof pitch !== "object") return { x: null, y: null };
  return {
    x: pitch.location?.x ?? pitch.x ?? null,
    y: pitch.location?.y ?? pitch.y ?? null,
  };
};

export const getPitchZone = (pitch, zoneType = "standard") => {
  const { x, y } = getPitchLocation(pitch);
  return calculateZoneFromLocation(x, y, zoneType);
};

export const withDerivedPitchZone = (pitch, zoneType = "standard") => {
  if (!pitch || typeof pitch !== "object") return pitch;
  const derivedZone = getPitchZone(pitch, pitch.zoneType || zoneType);
  return {
    ...pitch,
    zone: derivedZone,
  };
};

export const isPitchInZone = (x, y, zone, zoneType = "standard") => {
  const bounds = getZoneBounds(zoneType)?.[zone];
  if (!bounds) return false;
  const { xMin, xMax, yMin, yMax } = bounds;
  return x >= xMin && x <= xMax && y >= yMin && y <= yMax;
};

export const calculateZoneCenter = (zone, zoneType = "standard") => {
  const bounds = getZoneBounds(zoneType)?.[zone];
  if (!bounds) return { x: 0, y: 0 };
  const { xMin, xMax, yMin, yMax } = bounds;
  return {
    x: (xMin + xMax) / 2,
    y: (yMin + yMax) / 2,
  };
};

